// SPDX-License-Identifier: LicenseRef-Proprietary

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type { EvidenceItem, AbstractTriageBrief } from '../components/VaccineDashboard/lib/models';
import type { PubmedPaperDetails } from '../services/pubmedService';
import { getItem, setItem } from '../components/VaccineDashboard/lib/storage';

interface EvidenceStoreState {
  evidenceById: Record<string, EvidenceItem>; // Metadata only (no full abstracts)
  libraryOrder: string[]; // Array of PMIDs in order added
  newPapersThisSession: number;
  // In-memory cache for full details (not persisted)
  detailsCache: Record<string, PubmedPaperDetails>; // Full abstracts cached during session
}

interface EvidenceStoreActions {
  addEvidence: (item: EvidenceItem) => void;
  removeEvidence: (pmid: string) => void;
  resetNewThisSession: () => void;
  getEvidenceItems: () => EvidenceItem[];
  cacheDetails: (pmid: string, details: PubmedPaperDetails) => void;
  getCachedDetails: (pmid: string) => PubmedPaperDetails | undefined;
  clearDetailsCache: () => void;
  setAiBrief: (pmid: string, brief: AbstractTriageBrief) => void;
}

type EvidenceStore = EvidenceStoreState & EvidenceStoreActions;

const STORAGE_KEY = 'vaccineClient.evidenceLibrary';
const STORAGE_VERSION = 1;
const MAX_EVIDENCE_ITEMS = 500;

// Debounced save function
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
const debouncedSave = (state: EvidenceStoreState) => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  saveTimeout = setTimeout(() => {
    try {
      // Strip abstract field before saving (storage optimization)
      const itemsToSave: Record<string, Omit<EvidenceItem, 'abstract'>> = {};
      Object.entries(state.evidenceById).forEach(([pmid, item]) => {
        const { abstract, ...metadata } = item;
        itemsToSave[pmid] = metadata;
      });

      const dataToSave = {
        version: STORAGE_VERSION,
        evidenceById: itemsToSave,
        libraryOrder: state.libraryOrder,
        newPapersThisSession: 0, // Reset on save (only track during session)
      };

      setItem(STORAGE_KEY, dataToSave);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded for evidence library');
        // Could implement cleanup here if needed
      } else {
        console.warn('Failed to save evidence library state:', error);
      }
    }
  }, 500);
};

// Load from localStorage
const loadFromStorage = (): Partial<EvidenceStoreState> => {
  try {
    const saved = getItem(STORAGE_KEY, null);
    if (!saved) {
      return {
        evidenceById: {},
        libraryOrder: [],
        newPapersThisSession: 0,
        detailsCache: {},
      };
    }

    // Migration: Handle versioned storage
    const version = (saved as any).version || 1;
    if (version !== STORAGE_VERSION) {
      // Future migration logic here
    }

    // Restore evidence items (metadata only, no abstracts)
    const evidenceById: Record<string, EvidenceItem> = {};
    const savedItems = (saved as any).evidenceById || {};
    Object.entries(savedItems).forEach(([pmid, item]: [string, any]) => {
      evidenceById[pmid] = {
        ...item,
        abstract: undefined, // Ensure abstract is not restored
      } as EvidenceItem;
    });

    return {
      evidenceById,
      libraryOrder: (saved as any).libraryOrder || [],
      newPapersThisSession: 0, // Always start at 0 on load
      detailsCache: {}, // Cache is session-only, not persisted
    };
  } catch (error) {
    console.warn('Failed to load evidence library state:', error);
    return {
      evidenceById: {},
      libraryOrder: [],
      newPapersThisSession: 0,
      detailsCache: {},
    };
  }
};

export const useEvidenceStore = create<EvidenceStore>()(
  subscribeWithSelector((set, get) => {
    const loadedState = loadFromStorage();

    return {
      // Initial state
      evidenceById: loadedState.evidenceById || {},
      libraryOrder: loadedState.libraryOrder || [],
      newPapersThisSession: loadedState.newPapersThisSession || 0,
      detailsCache: {},

      // Actions
      addEvidence: (item: EvidenceItem) => {
        set((state) => {
          const existing = state.evidenceById[item.id];
          const isNew = !existing;

          // Strip abstract field (storage optimization)
          const { abstract, ...metadata } = item;

          if (isNew) {
            // Enforce MAX_EVIDENCE_ITEMS limit: remove oldest items if at limit
            let evidenceById = { ...state.evidenceById };
            let libraryOrder = [...state.libraryOrder];
            
            // Remove oldest items if we're at or over the limit
            while (libraryOrder.length >= MAX_EVIDENCE_ITEMS) {
              const oldestPmid = libraryOrder.shift();
              if (oldestPmid) {
                const { [oldestPmid]: removed, ...rest } = evidenceById;
                evidenceById = rest;
              }
            }

            // New item: add to libraryOrder and increment counter
            return {
              ...state,
              evidenceById: {
                ...evidenceById,
                [item.id]: metadata as EvidenceItem,
              },
              libraryOrder: [...libraryOrder, item.id],
              newPapersThisSession: state.newPapersThisSession + 1,
            };
          } else {
            // Existing item: update fields but DON'T increment counter
            return {
              ...state,
              evidenceById: {
                ...state.evidenceById,
                [item.id]: metadata as EvidenceItem,
              },
            };
          }
        });
        debouncedSave(get());
      },

      removeEvidence: (pmid: string) => {
        set((state) => {
          const { [pmid]: removed, ...evidenceById } = state.evidenceById;
          const { [pmid]: removedCache, ...detailsCache } = state.detailsCache;
          return {
            ...state,
            evidenceById,
            libraryOrder: state.libraryOrder.filter((id) => id !== pmid),
            detailsCache,
          };
        });
        debouncedSave(get());
      },

      resetNewThisSession: () => {
        set({ newPapersThisSession: 0 });
      },

      getEvidenceItems: () => {
        const state = get();
        return state.libraryOrder
          .map((pmid) => state.evidenceById[pmid])
          .filter((item): item is EvidenceItem => item !== undefined);
      },

      cacheDetails: (pmid: string, details: PubmedPaperDetails) => {
        set((state) => ({
          ...state,
          detailsCache: {
            ...state.detailsCache,
            [pmid]: details,
          },
        }));
      },

      getCachedDetails: (pmid: string) => {
        return get().detailsCache[pmid];
      },

      clearDetailsCache: () => {
        set((state) => ({
          ...state,
          detailsCache: {},
        }));
      },
      setAiBrief: (pmid: string, brief: AbstractTriageBrief) => {
        set((state) => {
          const item = state.evidenceById[pmid];
          if (!item) return state;

          return {
            ...state,
            evidenceById: {
              ...state.evidenceById,
              [pmid]: {
                ...item,
                aiBrief: brief,
              },
            },
          };
        });
        debouncedSave(get());
      },
    };
  })
);

